// src/pages/stockAdjustment/CreateStockAdjustmentPage.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package,
  Search,
  Plus,
  Edit2,
  Trash2,
  CheckCircle,
  AlertCircle,
  X,
  Scan,
  Loader2,
  ChevronDown,
  TrendingUp,
  TrendingDown,
  Minus
} from 'lucide-react';
import {
  getUserWarehouses,
  getTodaysAdjustments,
  searchProductsForAdjustment,
  addItemsToAdjustment,
  updateAdjustmentItem,
  deleteAdjustmentItem,
  finalizeStockAdjustment
} from '../../services/api/stockAdjustment';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { getImageUrl, handleImageError } from '../../utils/imageUtils';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import styles from './CreateStockAdjustmentPage.module.css';

const CreateStockAdjustmentPage = () => {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { success, error } = useAlert();
  const searchInputRef = useRef(null);
  const physicalCountInputRef = useRef(null);

  // Reference data
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');

  // Adjustment state
  const [currentAdjustment, setCurrentAdjustment] = useState(null);
  const [adjustmentItems, setAdjustmentItems] = useState([]);
  const [todayAdjustments, setTodayAdjustments] = useState([]);

  // Product search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef(null);

  // Add/Edit item modal state
  const [showItemModal, setShowItemModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [itemFormData, setItemFormData] = useState({
    physicalCount: '',
    notes: ''
  });
  const [editingItemId, setEditingItemId] = useState(null);

  // Dialog state
  const [showExistingAdjustmentsDialog, setShowExistingAdjustmentsDialog] = useState(false);
  const [showFinalizeDialog, setShowFinalizeDialog] = useState(false);

  // Loading states
  const [isLoadingWarehouses, setIsLoadingWarehouses] = useState(true);
  const [isLoadingAdjustments, setIsLoadingAdjustments] = useState(false);
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);

  // Load warehouses on mount
  useEffect(() => {
    loadWarehouses();
  }, []);

  // Load today's adjustments when warehouse changes
  useEffect(() => {
    if (selectedWarehouseId) {
      loadTodaysAdjustments();
    } else {
      setCurrentAdjustment(null);
      setAdjustmentItems([]);
      setTodayAdjustments([]);
    }
  }, [selectedWarehouseId]);

  // Product search with debounce
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchTerm.trim() && selectedWarehouseId) {
      searchTimeoutRef.current = setTimeout(() => {
        handleProductSearch(searchTerm);
      }, 300);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, selectedWarehouseId]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // F2 to focus search
      if (e.key === 'F2') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      // ESC to close modals/clear search
      if (e.key === 'Escape') {
        if (showItemModal) {
          handleCloseItemModal();
        } else if (showSearchResults) {
          setSearchTerm('');
          setShowSearchResults(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showItemModal, showSearchResults]);

  const loadWarehouses = async () => {
    try {
      setIsLoadingWarehouses(true);
      const response = await getUserWarehouses();
      setWarehouses(response.warehouses || []);

      // Auto-select if only one warehouse
      if (response.warehouses?.length === 1) {
        setSelectedWarehouseId(response.warehouses[0].id.toString());
      }
    } catch (err) {
      console.error('Failed to load warehouses:', err);
      error(t('stockAdjustment.failedToLoadWarehouses'));
    } finally {
      setIsLoadingWarehouses(false);
    }
  };

  const loadTodaysAdjustments = async () => {
    try {
      setIsLoadingAdjustments(true);
      const response = await getTodaysAdjustments(parseInt(selectedWarehouseId));
      const adjustments = response.adjustments || [];
      setTodayAdjustments(adjustments);

      if (adjustments.length === 0) {
        // No existing adjustments, ready to create new
        setCurrentAdjustment(null);
        setAdjustmentItems([]);
      } else if (adjustments.length === 1) {
        // Auto-select the only adjustment
        selectExistingAdjustment(adjustments[0]);
      } else {
        // Multiple adjustments, show dialog
        setShowExistingAdjustmentsDialog(true);
      }
    } catch (err) {
      console.error('Failed to load today\'s adjustments:', err);
      error(t('stockAdjustment.failedToLoadAdjustments'));
    } finally {
      setIsLoadingAdjustments(false);
    }
  };

  const selectExistingAdjustment = (adjustment) => {
    setCurrentAdjustment(adjustment);
    setAdjustmentItems(adjustment?.items || []);
    setShowExistingAdjustmentsDialog(false);
  };

  const startNewAdjustment = () => {
    setCurrentAdjustment(null);
    setAdjustmentItems([]);
    setShowExistingAdjustmentsDialog(false);
  };

  const handleProductSearch = async (term) => {
    try {
      setIsSearching(true);
      const response = await searchProductsForAdjustment({
        warehouseId: parseInt(selectedWarehouseId),
        search: term,
        limit: 10
      });
      setSearchResults(response.products || []);
      setShowSearchResults(true);
    } catch (err) {
      console.error('Product search error:', err);
      error(t('stockAdjustment.failedToSearchProducts'));
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleProductSelect = (product) => {
    // Check if product already exists in adjustment
    const exists = adjustmentItems.some(
      item => item.productId === product.productId && 
              item.variationId === product.variationId
    );

    if (exists) {
      error(t('stockAdjustment.duplicateItem'));
      return;
    }

    setSelectedProduct(product);
    setItemFormData({
      physicalCount: '',
      notes: ''
    });
    setEditingItemId(null);
    setShowItemModal(true);
    setSearchTerm('');
    setShowSearchResults(false);

    // Focus physical count input after modal opens
    setTimeout(() => {
      physicalCountInputRef.current?.focus();
    }, 100);
  };

  const handleEditItem = (item) => {
    setSelectedProduct({
      productId: item.productId,
      variationId: item.variationId,
      displayName: item.displayName,
      sku: item.sku,
      stockQuantity: item.recordedCount
    });
    setItemFormData({
      physicalCount: item.physicalCount.toString(),
      notes: item.notes || ''
    });
    setEditingItemId(item.id);
    setShowItemModal(true);

    setTimeout(() => {
      physicalCountInputRef.current?.focus();
      physicalCountInputRef.current?.select();
    }, 100);
  };

  const handleCloseItemModal = () => {
    setShowItemModal(false);
    setSelectedProduct(null);
    setItemFormData({ physicalCount: '', notes: '' });
    setEditingItemId(null);
  };

  const handleSubmitItem = async (e) => {
    e.preventDefault();

    if (!itemFormData.physicalCount || parseFloat(itemFormData.physicalCount) < 0) {
      error(t('stockAdjustment.physicalCountRequired'));
      return;
    }

    if (editingItemId) {
      // Update existing item
      try {
        setIsAddingItem(true);
        await updateAdjustmentItem(editingItemId, {
          physicalCount: parseFloat(itemFormData.physicalCount),
          notes: itemFormData.notes || null
        });

        // Update local state
        setAdjustmentItems(prev => prev.map(item => {
          if (item.id === editingItemId) {
            const variance = parseFloat(itemFormData.physicalCount) - parseFloat(item.recordedCount);
            return {
              ...item,
              physicalCount: parseFloat(itemFormData.physicalCount),
              variance,
              notes: itemFormData.notes || null
            };
          }
          return item;
        }));

        success(t('stockAdjustment.itemUpdated'));
        handleCloseItemModal();
      } catch (err) {
        console.error('Failed to update item:', err);
        error(err.response?.data?.message || t('stockAdjustment.failedToUpdateItem'));
      } finally {
        setIsAddingItem(false);
      }
    } else {
      // Add new item
      try {
        setIsAddingItem(true);
        const response = await addItemsToAdjustment({
          warehouseId: parseInt(selectedWarehouseId),
          adjustmentId: currentAdjustment?.id || null,
          forceNew: false,
          items: [
            {
              productId: selectedProduct.productId,
              variationId: selectedProduct.variationId || null,
              physicalCount: parseFloat(itemFormData.physicalCount),
              notes: itemFormData.notes || null
            }
          ]
        });

        // Update current adjustment if it was just created
        if (!currentAdjustment && response.adjustment) {
          setCurrentAdjustment({
            id: response.adjustment.id,
            adjustmentNumber: response.adjustment.adjustmentNumber,
            warehouseId: response.adjustment.warehouseId,
            adjustmentDate: response.adjustment.adjustmentDate
          });
        }

        // Add items to local state
        const newItems = response.addedItems.map(item => ({
          id: item.id,
          productId: item.productId,
          variationId: item.variationId,
          displayName: selectedProduct.displayName,
          sku: selectedProduct.sku,
          image: selectedProduct.image,
          recordedCount: item.recordedCount,
          physicalCount: item.physicalCount,
          variance: item.variance,
          notes: item.notes
        }));

        setAdjustmentItems(prev => [...prev, ...newItems]);
        success(t('stockAdjustment.itemAdded'));
        handleCloseItemModal();

        // Focus search input for next item
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 100);
      } catch (err) {
        console.error('Failed to add item:', err);
        error(err.response?.data?.message || t('stockAdjustment.failedToAddItem'));
      } finally {
        setIsAddingItem(false);
      }
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm(t('stockAdjustment.confirmDelete'))) {
      return;
    }

    try {
      await deleteAdjustmentItem(itemId);
      setAdjustmentItems(prev => prev.filter(item => item.id !== itemId));
      success(t('stockAdjustment.itemDeleted'));
    } catch (err) {
      console.error('Failed to delete item:', err);
      error(err.response?.data?.message || t('stockAdjustment.failedToDeleteItem'));
    }
  };

  const handleFinalize = () => {
    if (adjustmentItems.length === 0) {
      error(t('stockAdjustment.noItemsToFinalize'));
      return;
    }
    setShowFinalizeDialog(true);
  };

  const confirmFinalize = async () => {
    try {
      setIsFinalizing(true);
      await finalizeStockAdjustment(currentAdjustment.id);
      success(t('stockAdjustment.adjustmentFinalized'));
      navigate('/inventory/stock-adjustments');
    } catch (err) {
      console.error('Failed to finalize adjustment:', err);
      error(err.response?.data?.message || t('stockAdjustment.failedToFinalize'));
    } finally {
      setIsFinalizing(false);
      setShowFinalizeDialog(false);
    }
  };

  const calculateTotals = () => {
    const totalItems = adjustmentItems.length;
    const totalVariance = adjustmentItems.reduce((sum, item) => sum + parseFloat(item.variance), 0);
    return { totalItems, totalVariance };
  };

  const getVarianceDisplay = (variance) => {
    const value = parseFloat(variance);
    if (value === 0) {
      return { text: t('stockAdjustment.varianceZero'), icon: Minus, color: '#6b7280' };
    } else if (value > 0) {
      return { text: `+${value}`, icon: TrendingUp, color: '#16a34a' };
    } else {
      return { text: value.toString(), icon: TrendingDown, color: '#dc2626' };
    }
  };

  const totals = calculateTotals();
  const selectedWarehouse = warehouses.find(w => w.id.toString() === selectedWarehouseId);

  if (isLoadingWarehouses) {
    return (
      <div className={styles.stockAdjustmentPage}>
        <PageHeader
          title={t('stockAdjustment.createStockAdjustment')}
          description={t('stockAdjustment.pageDescription')}
          icon={Package}
        />

        <div className={styles.loadingState}>
          <Loader2 className={styles.spinning} size={48} />
          <h3>{t('stockAdjustment.loadingWarehouses')}</h3>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.stockAdjustmentPage}>
      {/* Header */}
      <PageHeader
        title={t('stockAdjustment.createStockAdjustment')}
        description={t('stockAdjustment.pageDescription')}
        icon={Package}
        actions={
          <button onClick={() => navigate('/inventory/stock-adjustments')} className={styles.backButton}>
            <X size={20} />
            {t('stockAdjustment.backToStockAdjustments')}
          </button>
        }
      />

      <div className={styles.pageContent}>
        {/* Warehouse Selection */}
        <div className={styles.warehouseSection}>
          <div className={styles.sectionCard}>
            <h2>
              <Package size={20} />
              {t('stockAdjustment.selectWarehouse')}
            </h2>
            <div className={styles.selectWrapper}>
              <select
                value={selectedWarehouseId}
                onChange={(e) => setSelectedWarehouseId(e.target.value)}
                className={styles.select}
                disabled={isLoadingAdjustments}
              >
                <option value="">{t('stockAdjustment.selectWarehousePlaceholder')}</option>
                {warehouses.map(warehouse => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.name}
                  </option>
                ))}
              </select>
              <ChevronDown className={styles.selectArrow} size={18} />
            </div>
            {isLoadingAdjustments && (
              <div className={styles.loadingIndicator}>
                <Loader2 className={styles.spinning} size={16} />
                <span>{t('stockAdjustment.loadingAdjustments')}</span>
              </div>
            )}
          </div>

          {/* Current Adjustment Info */}
          {currentAdjustment && (
            <div className={styles.adjustmentInfo}>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>{t('stockAdjustment.adjustmentNumber')}:</span>
                <strong>{currentAdjustment.adjustmentNumber}</strong>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>{t('stockAdjustment.warehouse')}:</span>
                <span>{selectedWarehouse?.name}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>{t('stockAdjustment.status')}:</span>
                <span className={styles.statusBadge}>{t('stockAdjustment.statusDraft')}</span>
              </div>
            </div>
          )}
        </div>

        {/* Product Search & Items */}
        {selectedWarehouseId && (
          <div className={styles.mainSection}>
            <div className={styles.searchSection}>
              <div className={styles.sectionCard}>
                <h2>
                  <Search size={20} />
                  {t('stockAdjustment.searchProducts')}
                </h2>
                <div className={styles.searchWrapper}>
                  <Search className={styles.searchIcon} size={18} />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onFocus={() => searchTerm && setShowSearchResults(true)}
                    placeholder={t('stockAdjustment.searchProductsPlaceholder')}
                    className={styles.searchInput}
                  />
                  {searchTerm && (
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setShowSearchResults(false);
                      }}
                      className={styles.clearButton}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>

                {/* Search Results */}
                {showSearchResults && (
                  <div className={styles.searchResults}>
                    {isSearching ? (
                      <div className={styles.searchLoading}>
                        <Loader2 className={styles.spinning} size={20} />
                        <span>{t('stockAdjustment.searchingProducts')}</span>
                      </div>
                    ) : searchResults.length > 0 ? (
                      <div className={styles.resultsList}>
                        {searchResults.map((product, index) => (
                          <div
                            key={`${product.productId}-${product.variationId || 'simple'}`}
                            className={styles.resultItem}
                            onClick={() => handleProductSelect(product)}
                          >
                            {product.image && (
                              <img
                                src={getImageUrl(product.image)}
                                alt={product.displayName}
                                className={styles.productImage}
                                onError={handleImageError}
                              />
                            )}
                            <div className={styles.productInfo}>
                              <div className={styles.productName}>{product.displayName}</div>
                              <div className={styles.productSku}>
                                {t('stockAdjustment.sku')}: {product.sku || t('stockAdjustment.noMatch')}
                              </div>
                            </div>
                            <div className={styles.productStock}>
                              <span className={styles.stockLabel}>{t('stockAdjustment.currentStock')}:</span>
                              <strong>{product.stockQuantity}</strong>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={styles.noResults}>
                        <AlertCircle size={16} />
                        <span>{t('stockAdjustment.noProductsFound')}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Keyboard Shortcuts */}
              <div className={styles.shortcutsCard}>
                <h3>{t('stockAdjustment.keyboardShortcuts')}</h3>
                <div className={styles.shortcutsList}>
                  <div className={styles.shortcutItem}>
                    <kbd>F2</kbd>
                    <span>{t('stockAdjustment.focusSearch')}</span>
                  </div>
                  <div className={styles.shortcutItem}>
                    <kbd>ESC</kbd>
                    <span>{t('stockAdjustment.clearSearch')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Items List */}
            <div className={styles.itemsSection}>
              <div className={styles.sectionCard}>
                <div className={styles.itemsHeader}>
                  <h2>
                    <Scan size={20} />
                    {t('stockAdjustment.adjustmentItems')}
                  </h2>
                  <div className={styles.itemsCount}>
                    {totals.totalItems} {totals.totalItems === 1 ? t('stockAdjustment.item') : t('stockAdjustment.items')}
                  </div>
                </div>

                {adjustmentItems.length > 0 ? (
                  <>
                    <div className={styles.itemsTable}>
                      <table>
                        <thead>
                          <tr>
                            <th>{t('stockAdjustment.itemNumber')}</th>
                            <th>{t('stockAdjustment.itemProduct')}</th>
                            <th>{t('stockAdjustment.itemRecordedCount')}</th>
                            <th>{t('stockAdjustment.itemPhysicalCount')}</th>
                            <th>{t('stockAdjustment.itemVariance')}</th>
                            <th>{t('stockAdjustment.itemNotes')}</th>
                            <th>{t('stockAdjustment.itemActions')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {adjustmentItems.map((item, index) => {
                            const varianceDisplay = getVarianceDisplay(item.variance);
                            const VarianceIcon = varianceDisplay.icon;

                            return (
                              <tr key={item.id}>
                                <td>{index + 1}</td>
                                <td>
                                  <div className={styles.itemProduct}>
                                    {item.image && (
                                      <img
                                        src={getImageUrl(item.image)}
                                        alt={item.displayName}
                                        className={styles.itemImage}
                                        onError={handleImageError}
                                      />
                                    )}
                                    <div>
                                      <div className={styles.itemName}>{item.displayName}</div>
                                      <div className={styles.itemSku}>{item.sku}</div>
                                    </div>
                                  </div>
                                </td>
                                <td className={styles.textCenter}>{item.recordedCount}</td>
                                <td className={styles.textCenter}>{item.physicalCount}</td>
                                <td className={styles.textCenter}>
                                  <div className={styles.varianceCell} style={{ color: varianceDisplay.color }}>
                                    <VarianceIcon size={16} />
                                    <strong>{varianceDisplay.text}</strong>
                                  </div>
                                </td>
                                <td>
                                  <div className={styles.notesCell}>
                                    {item.notes || '-'}
                                  </div>
                                </td>
                                <td>
                                  <div className={styles.actionButtons}>
                                    <button
                                      onClick={() => handleEditItem(item)}
                                      className={styles.editButton}
                                      title={t('stockAdjustment.edit')}
                                    >
                                      <Edit2 size={16} />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteItem(item.id)}
                                      className={styles.deleteButton}
                                      title={t('stockAdjustment.delete')}
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Totals */}
                    <div className={styles.totalsSection}>
                      <div className={styles.totalRow}>
                        <span className={styles.totalLabel}>{t('stockAdjustment.totalItems')}:</span>
                        <strong>{totals.totalItems}</strong>
                      </div>
                      <div className={styles.totalRow}>
                        <span className={styles.totalLabel}>{t('stockAdjustment.totalVariance')}:</span>
                        <strong
                          style={{
                            color: totals.totalVariance === 0 ? '#6b7280' :
                                   totals.totalVariance > 0 ? '#16a34a' : '#dc2626'
                          }}
                        >
                          {totals.totalVariance > 0 ? '+' : ''}{totals.totalVariance.toFixed(2)}
                        </strong>
                      </div>
                    </div>

                    {/* Finalize Button */}
                    <div className={styles.finalizeSection}>
                      <button
                        onClick={handleFinalize}
                        className={styles.finalizeButton}
                        disabled={isFinalizing}
                      >
                        <CheckCircle size={20} />
                        {t('stockAdjustment.finalizeAdjustment')}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className={styles.emptyState}>
                    <Package size={48} />
                    <h3>{t('stockAdjustment.noItemsAdded')}</h3>
                    <p>{t('stockAdjustment.addItemsToAdjustment')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Existing Adjustments Dialog */}
      {showExistingAdjustmentsDialog && (
        <div className={styles.dialogOverlay}>
          <div className={styles.dialogContent}>
            <div className={styles.dialogHeader}>
              <AlertCircle size={24} style={{ color: '#f59e0b' }} />
              <h3>{t('stockAdjustment.existingAdjustments')}</h3>
            </div>
            <div className={styles.dialogBody}>
              <p>{t('stockAdjustment.existingAdjustmentsMessage')}</p>
              <div className={styles.adjustmentsList}>
                {todayAdjustments.map(adj => (
                  <div
                    key={adj.id}
                    className={styles.adjustmentOption}
                    onClick={() => selectExistingAdjustment(adj)}
                  >
                    <div className={styles.adjustmentDetails}>
                      <strong>{adj.adjustmentNumber}</strong>
                      <span>{adj.totalItems || 0} {t('stockAdjustment.items')}</span>
                    </div>
                    <ChevronDown size={16} style={{ transform: 'rotate(-90deg)' }} />
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.dialogActions}>
              <button onClick={startNewAdjustment} className={styles.newButton}>
                <Plus size={16} />
                {t('stockAdjustment.startNewAdjustment')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Item Modal */}
      {showItemModal && (
        <div className={styles.dialogOverlay}>
          <div className={styles.dialogContent}>
            <div className={styles.dialogHeader}>
              <Package size={24} />
              <h3>
                {editingItemId 
                  ? t('stockAdjustment.editItemTitle')
                  : t('stockAdjustment.addItemTitle')
                }
              </h3>
              <button onClick={handleCloseItemModal} className={styles.closeButton}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmitItem}>
              <div className={styles.dialogBody}>
                <div className={styles.formGroup}>
                  <label>{t('stockAdjustment.productName')}</label>
                  <input
                    type="text"
                    value={selectedProduct?.displayName || ''}
                    disabled
                    className={styles.input}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>{t('stockAdjustment.recordedCount')}</label>
                  <input
                    type="text"
                    value={selectedProduct?.stockQuantity || 0}
                    disabled
                    className={styles.input}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>{t('stockAdjustment.physicalCount')} *</label>
                  <input
                    ref={physicalCountInputRef}
                    type="number"
                    value={itemFormData.physicalCount}
                    onChange={(e) => setItemFormData(prev => ({ ...prev, physicalCount: e.target.value }))}
                    min="0"
                    step="0.01"
                    className={styles.input}
                    required
                  />
                </div>

                {itemFormData.physicalCount && (
                  <div className={styles.formGroup}>
                    <label>{t('stockAdjustment.variance')}</label>
                    <div className={styles.variancePreview}>
                      {(() => {
                        const variance = parseFloat(itemFormData.physicalCount) - (selectedProduct?.stockQuantity || 0);
                        const display = getVarianceDisplay(variance);
                        const Icon = display.icon;
                        return (
                          <div style={{ color: display.color, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Icon size={20} />
                            <strong style={{ fontSize: '1.25rem' }}>{display.text}</strong>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}

                <div className={styles.formGroup}>
                  <label>{t('stockAdjustment.notes')}</label>
                  <textarea
                    value={itemFormData.notes}
                    onChange={(e) => setItemFormData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder={t('stockAdjustment.notesPlaceholder')}
                    className={styles.textarea}
                    rows={3}
                  />
                </div>
              </div>

              <div className={styles.dialogActions}>
                <button
                  type="button"
                  onClick={handleCloseItemModal}
                  className={styles.cancelButton}
                  disabled={isAddingItem}
                >
                  {t('stockAdjustment.cancel')}
                </button>
                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={isAddingItem}
                >
                  {isAddingItem ? (
                    <>
                      <Loader2 className={styles.spinning} size={16} />
                      {editingItemId ? t('stockAdjustment.updatingItem') : t('stockAdjustment.addingItem')}
                    </>
                  ) : (
                    <>
                      <CheckCircle size={16} />
                      {editingItemId ? t('stockAdjustment.updateItem') : t('stockAdjustment.addItem')}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Finalize Confirmation Dialog */}
      {showFinalizeDialog && (
        <div className={styles.dialogOverlay}>
          <div className={styles.dialogContent}>
            <div className={styles.dialogHeader}>
              <AlertCircle size={24} style={{ color: '#f59e0b' }} />
              <h3>{t('stockAdjustment.confirmFinalizeTitle')}</h3>
            </div>
            <div className={styles.dialogBody}>
              <p>{t('stockAdjustment.confirmFinalize')}</p>
              <div className={styles.summaryBox}>
                <div className={styles.summaryRow}>
                  <span>{t('stockAdjustment.totalItems')}:</span>
                  <strong>{totals.totalItems}</strong>
                </div>
                <div className={styles.summaryRow}>
                  <span>{t('stockAdjustment.totalVariance')}:</span>
                  <strong
                    style={{
                      color: totals.totalVariance === 0 ? '#6b7280' :
                             totals.totalVariance > 0 ? '#16a34a' : '#dc2626'
                    }}
                  >
                    {totals.totalVariance > 0 ? '+' : ''}{totals.totalVariance.toFixed(2)}
                  </strong>
                </div>
              </div>
            </div>
            <div className={styles.dialogActions}>
              <button
                onClick={() => setShowFinalizeDialog(false)}
                className={styles.cancelButton}
                disabled={isFinalizing}
              >
                {t('stockAdjustment.no')}
              </button>
              <button
                onClick={confirmFinalize}
                className={styles.confirmButton}
                disabled={isFinalizing}
              >
                {isFinalizing ? (
                  <>
                    <Loader2 className={styles.spinning} size={16} />
                    {t('stockAdjustment.finalizing')}
                  </>
                ) : (
                  <>
                    <CheckCircle size={16} />
                    {t('stockAdjustment.yes')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateStockAdjustmentPage;